import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';

import { useCommonTranslation } from '../../../hooks/useCommonTranslation';

import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Paper,
} from '@mui/material';
import { getSmsAll } from '../../../data/messages.slice';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { MessageInfo } from '../../../data/models/monitoring-messages.model';

const PREFIX = 'MessagesTable';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  subheader: `${PREFIX}-subheader`,
  tabheader: `${PREFIX}-tabheader`,
  tabcell: `${PREFIX}-tabcell`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    minWidth: 400,
    width: '100%',
  },

  [`& .${classes.header}`]: {
    fontFamily: 'Bitter',
    fontSize: '30px',
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: '40px',
    letterSpacing: '0.25px',
    paddingBottom: theme.spacing(5),
    paddingLeft: theme.spacing(1),
  },

  [`& .${classes.subheader}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
    letterSpacing: '0.17px',
  },

  [`& .${classes.tabheader}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
    letterSpacing: '0.4px',
  },

  [`& .${classes.tabcell}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
    letterSpacing: '0.17px',
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}
const formatUtcString = (utcString: string) => {
  const date = new Date(utcString);
  // Options to format the time string
  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };
  return date.toLocaleTimeString('en-US', options);
};

interface MessagesTableProps {
  messages: MessageInfo[];
  name: string; // Add this line
}

const MessagesTable: React.FC<MessagesTableProps> = ({ messages, name }) => {
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell className={classes.subheader}>Received</TableCell>
          <TableCell className={classes.subheader}>Type</TableCell>
          <TableCell className={classes.subheader}>Name</TableCell>
          <TableCell className={classes.subheader}>Message</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {messages.map((message) => (
          <TableRow key={message.smsMessageTrackingID}>
            <TableCell className={classes.tabcell}>
              {formatUtcString(message.createdOnUTC)}
            </TableCell>
            <TableCell className={classes.tabcell}>Passenger</TableCell>
            <TableCell className={classes.tabcell}>{name}</TableCell>
            <TableCell className={classes.tabcell}>
              {message.smsMessage}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

// Inside MessagesSection component, use the MessagesTable for each tab content

export default function MessagesSection() {
  const [value, setValue] = React.useState(0);
  const t = useCommonTranslation();

  const { messages, name, totalItems, totalPages, currentPage, loading } =
    useAppSelector((state) => state.message);
  const dispatch = useAppDispatch();

  interface GetSmsAllParams {
    phoneNumber: string;
    page: number;
    pageSize: number;
  }

  // F1REQ-2409 will make following initialization of page and phoneNumber as per dynamic input values for Inbox + All tabs
  const initialState: GetSmsAllParams = {
    phoneNumber: '19132844657',
    page: 1,
    pageSize: 5,
  };

  useEffect(() => {
    dispatch(getSmsAll(initialState));
  }, [dispatch]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);

    // Check if the "All" tab is selected
    if (newValue === 1) {
      const allMessagesState = {
        ...initialState,
        pageSize: 10,
      };

      dispatch(getSmsAll(allMessagesState));
    }
    // Check if the "Inbox" tab is selected
    if (newValue === 0) {
      const inboxMessagesState = {
        ...initialState,
        pageSize: 5,
      };

      dispatch(getSmsAll(inboxMessagesState));
    }
  };

  return (
    <StyledBox className={classes.container}>
      <MessagesTable messages={messages} name={name} />

      {loading && (
        <Typography>{t('monitoring.monitoringLoadingMessages')}</Typography>
      )}
      <Button sx={{ mt: 2 }}>{t('monitoring.monitoringMore')}</Button>
    </StyledBox>
  );
}
